import { lazy, useCallback } from 'react';
import { Navigate, Outlet } from 'react-router-dom';

// project-imports
import MainLayout from 'layout/MainLayout';
// import CommonLayout from 'layout/CommonLayout';
import Loadable from 'components/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import { useAllowedControllers, getFirstAccessibleRoute } from 'utils/menu/accessController';
import { DesignStudioProvider } from 'contexts/DesignStudioContext';
import ImageViewerSecondaryScreen from 'sections/review/image-viewer/secondary-screen';
import VerificationImageViewerSecondaryScreen from 'sections/review/verification/image-viewer/secondary-screen';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import RedirectHandler from 'sections/auth/auth-forms/ExternalAuthRedirect';
import AccessDenied from 'pages/maintenance/access-denied';

const UserProfilePage = Loadable(lazy(() => import('pages/settings/UserProfilePage')));
const NotFound = Loadable(lazy(() => import('pages/maintenance/404')));
const MaintenanceError500 = Loadable(lazy(() => import('pages/maintenance/500')));
const MaintenanceUnderConstruction = Loadable(lazy(() => import('pages/maintenance/under-construction')));
const SamplePage = Loadable(lazy(() => import('pages/extra-pages/sample-page')));
const AdminDashboard = Loadable(lazy(() => import('pages/dashboard/admin')));
const ProcessDashboard = Loadable(lazy(() => import('pages/dashboard/process')));
const AccessManagementAdmin = Loadable(lazy(() => import('pages/admin/accessManagement')));
const AssignAdmin = Loadable(lazy(() => import('pages/admin/assign')));
// const PlatformSettingsAdmin = Loadable(lazy(() => import('pages/admin/platformSettings')));
const ChangeUserPassword = Loadable(lazy(() => import('pages/auth/change-user-password')));
const Changelog = Loadable(lazy(() => import('changelog/Changelog')));
const ClintSettings = Loadable(lazy(() => import('pages/admin/clientSettings')));
const PlatformManagement = Loadable(lazy(() => import('pages/admin/platformManagement')));

// Document Pages Import

const DocumentList = Loadable(lazy(() => import('pages/design/document/list')));
const DocumentLevelSettings = Loadable(lazy(() => import('pages/design/document/settings')));
const DocumentUpload = Loadable(lazy(() => import('pages/design/document/upload')));
const DocumentConfiguration = Loadable(lazy(() => import('pages/design/document/configuration')));
const DocumentConfigurationSecondaryScreen = Loadable(lazy(() => import('pages/design/document/configuration/secondary-screen')));
const MasterDataManagement = Loadable(lazy(() => import('pages/design/master-data')));
const ModalManagementData = Loadable(lazy(() => import('pages/review/model-management')));
const MoveToProd = Loadable(lazy(() => import('pages/admin/move-to-prod')));
const ModalTrainingData = Loadable(lazy(() => import('pages/review/modelTraining')));
const ConfidenceModel = Loadable(lazy(() => import('pages/design/confidence-model')));
const DesignStudio = Loadable(lazy(() => import('pages/design-studio/index')));
const DataPointConfiguration = Loadable(lazy(() => import('pages/design/document/data-point-configuration')));

//reports Page import
const Reports = Loadable(lazy(() => import('pages/report/reports')));

//Review Page import
const PredictionManagement = Loadable(lazy(() => import('pages/review/predictionManagement')));
const AssuranceManagement = Loadable(lazy(() => import('pages/review/assuranceManagement')));
const ExceptionManagement = Loadable(lazy(() => import('pages/review/exceptionManagement')));
const VerificationManagement = Loadable(lazy(() => import('pages/review/verificationManagement')));
const AssuranceImageViewerPage = Loadable(lazy(() => import('pages/review/image-viewer')));
const VerificationImageViewerPage = Loadable(lazy(() => import('pages/review/image-viewer/VerificationImageViewerPage')));
const ProcessReport = Loadable(lazy(() => import('pages/report/processReport')));

// ==============================|| MAIN ROUTES ||============================== //
const alwaysAccessibleRoutes = ['sample-page', 'change-password', 'changelog'];

const MainRoutes = () => {
  const allowedControllers = useAllowedControllers();
  // Determine the first accessible route for /dashboard
  const dashboardRoutes = ['dashboard-admin', 'dashboard-process'];
  const firstAccessibleDashboardRoute = getFirstAccessibleRoute(allowedControllers, dashboardRoutes);
  const authToken = useSelector((state: RootState) => state.auth.decodedAuthToken);

  const renderNoController = useCallback(() => {
    return authToken ? <AccessDenied /> : <></>;
  }, [authToken]);

  return [
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: 'dashboard',
          children: [
            {
              path: '',
              element: authToken ? <Navigate to={`/${firstAccessibleDashboardRoute.replace('dashboard-', 'dashboard/')}`} replace /> : <></>
            },
            {
              path: 'admin',
              element: allowedControllers.includes('dashboard-admin') ? <AdminDashboard /> : renderNoController()
            },
            {
              path: 'process',
              element: allowedControllers.includes('dashboard-process') ? <ProcessDashboard /> : renderNoController()
            }
          ]
        },
        {
          path: 'design',
          children: [
            {
              path: '',
              element: (
                <Navigate
                  to={
                    allowedControllers.includes('design-document-list')
                      ? '/design/document-list'
                      : allowedControllers.includes('design-master-data')
                      ? '/design/master-data'
                      : allowedControllers.includes('training-confidence-model')
                      ? '/design/confidence-model'
                      : '/design/document-list'
                  }
                  replace
                />
              )
            },
            {
              path: 'document-list',
              element: allowedControllers.includes('design-document-list') ? <DocumentList /> : renderNoController()
            },
            {
              path: 'document-settings',
              element: allowedControllers.includes('design-document-list') ? <DocumentLevelSettings /> : renderNoController()
            },
            {
              path: 'document-settings/:processId/:docTypeID',
              element: allowedControllers.includes('design-document-list') ? <DocumentLevelSettings /> : renderNoController()
            },
            {
              path: 'file-configuration/:processId/:docTypeID/:batchId/:fileId',
              element: <DocumentConfiguration />
            },
            {
              path: 'master-data',
              element: allowedControllers.includes('design-master-data') ? <MasterDataManagement /> : renderNoController()
            },

            {
              path: 'file-upload/:processId/:docTypeID',
              element: allowedControllers.includes('design-document-list') ? <DocumentUpload /> : renderNoController()
            },
            {
              path: 'design-studio/:processID/:docTypeID/:fieldID',
              element: allowedControllers.includes('design-document-list') ? (
                <DesignStudioProvider>
                  <DesignStudio />
                </DesignStudioProvider>
              ) : (
                renderNoController()
              )
            },
            {
              path: 'data-point-configuration/:processId/:docTypeID',
              element: allowedControllers.includes('design-document-list') ? <DataPointConfiguration /> : renderNoController()
            }
          ]
        },
        {
          path: 'report',
          children: [
            {
              path: '',
              element: (
                <Navigate
                  to={
                    allowedControllers.includes('report-admin')
                      ? '/report/process-report'
                      : allowedControllers.includes('report-system')
                      ? '/report/system-report'
                      : '/report/process-report'
                  }
                  replace
                />
              )
            },
            {
              path: 'system-report',
              element: allowedControllers.includes('design-document-list') ? <Reports /> : renderNoController()
            },
            {
              path: 'process-report',
              element: allowedControllers.includes('report-admin') ? <ProcessReport /> : renderNoController()
            }
          ]
        },
        {
          path: 'admin',
          children: [
            {
              path: '',
              element: (
                <Navigate
                  to={
                    allowedControllers.includes('admin-access-management')
                      ? '/admin/access-management'
                      : allowedControllers.includes('admin-assign')
                      ? '/admin/assign'
                      : allowedControllers.includes('admin-client-settings')
                      ? '/admin/client-settings'
                      : allowedControllers.includes('admin-platform-management')
                      ? '/admin/platform-management'
                      : allowedControllers.includes('admin-model-portablilty')
                      ? '/admin/model-portablilty'
                      : allowedControllers.includes('training-model-management')
                      ? '/admin/model-management'
                      : '/admin/access-management'
                  }
                  replace
                />
              )
            },
            {
              path: 'access-management',
              element: allowedControllers.includes('admin-access-management') ? <AccessManagementAdmin /> : renderNoController()
            },
            {
              path: 'assign',
              element: allowedControllers.includes('admin-assign') ? <AssignAdmin /> : renderNoController()
            },
            {
              path: 'platform-management',
              element: <PlatformManagement />
              // element: allowedControllers.includes('admin-platform-management') ? <PlatformSettingsAdmin /> : renderNoController()
            },
            {
              path: 'client-settings',
              element: allowedControllers.includes('admin-client-settings') ? <ClintSettings /> : renderNoController()
            },

            {
              path: 'model-portablilty',
              element: allowedControllers.includes('admin-model-portablilty') ? <MoveToProd /> : renderNoController()
            }
          ]
        },

        {
          path: 'review',
          element: <Outlet />,
          children: [
            {
              path: '',
              element: (
                <Navigate
                  to={
                    allowedControllers.includes('review-prediction')
                      ? '/review/prediction'
                      : allowedControllers.includes('training-model-training')
                      ? '/review/model-training'
                      : allowedControllers.includes('review-assurance')
                      ? '/review/assurance'
                      : allowedControllers.includes('review-exception')
                      ? '/review/exception'
                      : allowedControllers.includes('review-verification')
                      ? '/review/verification'
                      : '/review/prediction'
                  }
                  replace
                />
              )
            },
            {
              path: 'prediction',
              element: allowedControllers.includes('review-prediction') ? <PredictionManagement /> : renderNoController()
            },
            {
              path: 'assurance',
              element: allowedControllers.includes('review-assurance') ? <AssuranceManagement /> : renderNoController()
            },
            {
              path: 'exception',
              element: allowedControllers.includes('review-exception') ? <ExceptionManagement /> : renderNoController()
            },
            {
              path: 'image-viewer/verification/:docTypeId/:batchId?/:fileId?',
              element: <VerificationImageViewerPage key="verification-viewer" />
            },
            {
              path: 'image-viewer/:batchId/:fileId',
              element: <AssuranceImageViewerPage key="assurance-viewer" />
            },
            {
              path: 'verification',
              element: allowedControllers.includes('review-verification') ? <VerificationManagement /> : renderNoController()
            }
          ]
        },
        {
          path: 'training',
          children: [
            {
              path: '',
              element: (
                <Navigate
                  to={
                    allowedControllers.includes('training-model-management')
                      ? '/training/model-management'
                      : allowedControllers.includes('training-model-training')
                      ? '/training/model-training'
                      : allowedControllers.includes('training-confidence-model')
                      ? '/training/confidence-model'
                      : '/training/model-management'
                  }
                  replace
                />
              )
            },

            {
              path: 'model-training',
              element: allowedControllers.includes('training-model-training') ? <ModalTrainingData /> : <ModalTrainingData />
            },
            {
              path: 'model-management',
              element: allowedControllers.includes('training-model-management') ? <ModalManagementData /> : renderNoController()
            },
            {
              path: 'confidence-model',
              element: allowedControllers.includes('training-confidence-model') ? <ConfidenceModel /> : renderNoController()
            }
          ]
        },
        ...alwaysAccessibleRoutes.map((route) => ({
          path: route,
          element: {
            'sample-page': <SamplePage />,
            'change-password': <ChangeUserPassword />,
            changelog: <Changelog />
          }[route]
        })),
        // Settings routes
        {
          path: 'general-settings',
          element: (
            <AuthGuard>
              <UserProfilePage />
            </AuthGuard>
          )
        },
        {
          path: '*',
          element: <NotFound />
        }
      ]
    },
    {
      path: '/maintenance',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: '500',
          element: <MaintenanceError500 />
        },
        {
          path: 'under-construction',
          element: <MaintenanceUnderConstruction />
        },
        {
          path: 'access-denied',
          element: <AccessDenied />
        }
      ]
    },
    {
      path: '/document-configuration-secondary-view/:processId/:docTypeID/:batchId/:fileId',
      element: (
        <AuthGuard>
          <DocumentConfigurationSecondaryScreen />
        </AuthGuard>
      )
    },
    {
      path: '/image-viewer/:fileId',
      element: (
        <AuthGuard>
          <DocumentConfigurationSecondaryScreen />
        </AuthGuard>
      )
    },
    {
      path: '/image-viewer-secondary-view/:batchId/:fileId',
      element: (
        <AuthGuard>
          <ImageViewerSecondaryScreen />
        </AuthGuard>
      )
    },
    {
      path: '/verification-image-viewer-secondary-view/:docTypeId/:batchId?/:fileId?',
      element: (
        <AuthGuard>
          <VerificationImageViewerSecondaryScreen />
        </AuthGuard>
      )
    },
    {
      path: '/validate',
      element: (
        <AuthGuard>
          <RedirectHandler />
        </AuthGuard>
      )
    }
  ];
};

export default MainRoutes;
